import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';

import EthereumIcon from '@/assets/icons/ethereum.svg?react';
import PixIcon from '@/assets/icons/pix.svg?react';
import PolygonIcon from '@/assets/icons/polygon.svg?react';
import SPEIIcon from '@/assets/icons/spei.svg?react';
import TRON from '@/assets/icons/tron.svg?react';
import { useTranslator } from '@/hooks/useTranslator';
import { PaymentRailType } from '@/types/currency';
import { FundingMethodElements } from '@/types/fundingMethodElements';

interface Props {
  readonly fundingMethod: PaymentRailType;
}

export const FundingMethodChip: React.FC<Props> = (props: Props): React.ReactElement => {
  const { fundingMethod } = props;

  const tr = useTranslator();
  const Icon = React.useMemo(():
    | React.FunctionComponent<React.SVGProps<SVGSVGElement>>
    | undefined => {
    return fundingMethodConstants[fundingMethod]?.Icon;
  }, [fundingMethod]);
  const name = React.useMemo(
    (): string => fundingMethodConstants[fundingMethod]?.label ?? 'Unknown',
    [fundingMethod],
  );

  if (fundingMethod === PaymentRailType.unset) {
    return (
      <Box sx={styles.chip}>
        <Box sx={styles.animatedIcon} />
        <Box sx={styles.animatedText} />
      </Box>
    );
  }

  return (
    <Box sx={styles.chip}>
      {Icon ? <Icon width={16} height={16} /> : null}
      <Typography fontSize={16} fontWeight={400} lineHeight="24px" color="custom.LightGray">
        {tr('FUNDING_WITH', name)}
      </Typography>
    </Box>
  );
};

type EmptyFundingMethodChipProps = {
  readonly label: string;
};

export const EmptyFundingMethodChip: React.FC<EmptyFundingMethodChipProps> = (
  props: EmptyFundingMethodChipProps,
): React.ReactElement => {
  const { label } = props;
  const tr = useTranslator();

  return (
    <Box sx={styles.chip}>
      <Typography fontSize={16} fontWeight={400} lineHeight="24px" color="custom.LightGray">
        {tr(label)}
      </Typography>
    </Box>
  );
};

const fundingMethodConstants: Partial<{ [key in PaymentRailType]: FundingMethodElements }> = {
  [PaymentRailType.PIX]: {
    Icon: PixIcon,
    label: 'Pix',
  },
  [PaymentRailType.SPEI]: {
    Icon: SPEIIcon,
    label: 'SPEI',
  },
  [PaymentRailType.ACH]: {
    label: 'ACH',
  },
  [PaymentRailType.Wire]: {
    label: 'Wire',
  },
  [PaymentRailType.Swift]: {
    label: 'Swift',
  },
  [PaymentRailType.RTP]: {
    label: 'RTP',
  },
  [PaymentRailType.Polygon]: {
    Icon: PolygonIcon,
    label: 'Polygon',
  },
  [PaymentRailType.Ethereum]: {
    Icon: EthereumIcon,
    label: 'Ethereum',
  },
  [PaymentRailType.TRON]: {
    Icon: TRON,
    label: 'Tron',
  },
  [PaymentRailType.LocalExchange]: {
    label: 'Caliza Wallet',
  },
  [PaymentRailType.CalizaWallet]: {
    label: 'Caliza Wallet',
  },
  [PaymentRailType.unset]: {
    label: 'Unknown',
  },
};

const styles: Record<string, SystemStyleObject> = {
  chip: {
    display: 'flex',
    alignItems: 'center',
    height: 32,
    boxSizing: 'content-box',
    px: 0.5,
    gap: 0.5,
  },
  animatedIcon: {
    width: 16,
    height: 16,
    borderRadius: 2,
    animation: 'skeleton 1s infinite',
    backgroundColor: 'custom.Skeleton',
  },
  animatedText: {
    height: 16,
    width: 140,
    animation: 'skeleton 1s infinite',
    backgroundColor: 'custom.Skeleton',
    borderRadius: 2,
  },
};
